import { api } from './api'

export interface Contract {
  id: string
  project_id: string
  organization_id?: string
  name: string
  contract_type: string
  status: 'draft' | 'active' | 'under_review' | 'expired' | 'terminated'
  party_a_name: string
  party_a_type: string
  party_b_name: string
  party_b_type: string
  contract_value?: number
  start_date?: string
  end_date?: string
  execution_date?: string
  current_version_id?: string
  current_version_number: number
  risk_score?: number
  risk_factors?: any
  notice_deadlines?: any[]
  milestone_dates?: any[]
  jurisdiction?: string
  governing_law?: string
  compliance_checked?: boolean
  compliance_issues?: any[]
  created_at: string
  updated_at: string
  extracted_text?: string
}

export interface CreateContractRequest {
  project_id: string
  name: string
  contract_type: string
  party_a_name: string
  party_a_type: string
  party_b_name: string
  party_b_type: string
  contract_value?: number
  start_date?: string
  end_date?: string
  execution_date?: string
  jurisdiction?: string
  governing_law?: string
}

export interface ContractAnalysisResponse {
  contract_id: string
  extracted_text?: string
  clauses: ContractClause[]
  payment_terms: PaymentTerm[]
  risk_assessment?: RiskAssessment
  processing_time_ms: number
}

export interface ContractClause {
  id: string
  clause_type: string
  title: string
  text: string
  risk_level: 'low' | 'medium' | 'high' | 'critical'
  position: number
  issues?: string[]
}

export interface PaymentTerm {
  id: string
  term_type: string
  description: string
  amount?: number
  due_date?: string
  conditions?: string
}

export interface RiskAssessment {
  id: string
  overall_risk_score: number
  risk_factors: {
    category: string
    severity: string
    description: string
    mitigation?: string
  }[]
  high_risk_clauses: number
  missing_clauses: string[]
  recommendations: string[]
}

export const contractsService = {
  // Get all contracts for the organization
  async getAllContracts(): Promise<Contract[]> {
    const response = await api.get('/contracts')
    return response.data
  },

  // Get all contracts for a project
  async getProjectContracts(projectId: string): Promise<Contract[]> {
    const response = await api.get(`/contracts/project/${projectId}`)
    return response.data
  },

  // Get single contract details
  async getContract(contractId: string): Promise<Contract> {
    const response = await api.get(`/contracts/${contractId}`)
    return response.data
  },

  // Create new contract
  async createContract(data: CreateContractRequest): Promise<Contract> {
    const response = await api.post('/contracts', data)
    return response.data
  },

  // Upload contract document
  async uploadContract(contractId: string, file: File): Promise<void> {
    const formData = new FormData()
    formData.append('file', file)
    
    await api.post(`/contracts/${contractId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  // Analyze contract for risks and issues
  async analyzeContract(contractId: string): Promise<ContractAnalysisResponse> {
    const response = await api.post('/contracts/analyze', { 
      contract_id: contractId,
      extract_clauses: true,
      assess_risk: true
    })
    return response.data
  }
}